import { useCallback } from "react";
import { useAppSelector } from "../Redux/Hooks";

export interface UsePermissionsResult {
  isStudioOwner: boolean;
  isSubUser: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasModule: (moduleKey: string) => boolean;
  permissions: string[];
  modules: string[];
  permissionsLoading: boolean;
  studioRole: { id: number; name: string } | null;
}

/**
 * Hook to check effective studio permissions.
 * Studio Owner (role=3) has universal bypass access.
 * Sub-users (role=4) are gated strictly by assigned permission keys.
 */
export const usePermissions = (): UsePermissionsResult => {
  const user = useAppSelector((state) => state.auth.user);
  const { myPermissions, loading } = useAppSelector((state) => state.role);

  const isStudioOwner = user?.role === 3 || myPermissions?.is_studio_owner === true;
  const isSubUser = user?.role === 4;
  const permissions = myPermissions?.permissions || [];
  const modules = myPermissions?.modules || [];
  const permissionsLoading = loading.permissions;

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (isStudioOwner) return true;
      return permissions.includes(permission);
    },
    [isStudioOwner, permissions]
  );

  const hasAnyPermission = useCallback(
    (perms: string[]): boolean => {
      if (isStudioOwner) return true;
      return perms.some((p) => permissions.includes(p));
    },
    [isStudioOwner, permissions]
  );

  const hasAllPermissions = useCallback(
    (perms: string[]): boolean => {
      if (isStudioOwner) return true;
      return perms.every((p) => permissions.includes(p));
    },
    [isStudioOwner, permissions]
  );

  const hasModule = useCallback(
    (moduleKey: string): boolean => {
      if (isStudioOwner) return true;
      const normalized = moduleKey.toUpperCase();
      return modules.includes(normalized);
    },
    [isStudioOwner, modules]
  );

  return {
    isStudioOwner,
    isSubUser,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasModule,
    permissions,
    modules,
    permissionsLoading,
    studioRole: myPermissions?.studio_role || null,
  };
};

export default usePermissions;
